'use client';

import { c as _c } from "react/compiler-runtime";
import type { Address } from '@morpho-org/blue-sdk';
import { parseApiAsset } from '@morpho-org/dapp-core';
import { isDefined } from '@morpho-org/morpho-ts';
import { LargeAssetInput } from '@morpho-org/ui-v6/client';
import { useDialogContext } from '@morpho-org/ui-v6/contexts';
import { WITHDRAW_BUNDLE_NOT_AUTHORIZED_MESSAGE } from '@/components/common/BundleAuthorizationCallout/messages';
import { ErrorServiceUnavailable } from '@/components/common/Error';
import SectionErrorBoundary from '@/components/common/SectionErrorBoundary';
import { getParsedValue } from '@/helpers/formatters';
import type { VaultV1ActionPanelData } from '../vaultV1ActionPanelData';
import { useVaultV1WithdrawLogic } from '../../hooks/useVaultV1WithdrawLogic';
import VaultV1OperationShell, { VaultV1FieldError } from './VaultV1OperationShell';
import { VaultV1WithdrawReviewDialog } from './VaultV1ReviewDialog';
import { VaultV1Summary } from './VaultV1Summary';
interface VaultV1OperationWithdrawProps {
  vaultData: VaultV1ActionPanelData | null;
  vaultAddress: Address;
}
const VaultV1OperationWithdraw = t0 => {
  const $ = _c(45);
  const {
    vaultData,
    vaultAddress
  } = t0;
  const {
    toggleDialog
  } = useDialogContext();
  const t1 = vaultData?.asset;
  let t2;
  if ($[0] !== t1) {
    t2 = getParsedValue(t1, parseApiAsset);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const vaultAsset = t2;
  let t3;
  if ($[2] !== vaultAddress || $[3] !== vaultData) {
    t3 = {
      vaultData,
      vaultAddress
    };
    $[2] = vaultAddress;
    $[3] = vaultData;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const {
    inputAmount,
    setInputAmount,
    isMax,
    onMaxClick,
    maxWithdraw,
    finalPosition,
    isLoading,
    disabledLabel,
    canTransfer,
    bundleCanTransfer,
    tooltipMessage,
    userVaultAssets,
    error,
    resetInput,
    refetchPosition
  } = useVaultV1WithdrawLogic(t3);
  let t4;
  if ($[5] !== refetchPosition || $[6] !== resetInput) {
    t4 = () => {
      resetInput();
      refetchPosition();
    };
    $[5] = refetchPosition;
    $[6] = resetInput;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const onTxSuccess = t4;
  let t5;
  if ($[8] !== inputAmount || $[9] !== isMax || $[10] !== onTxSuccess || $[11] !== toggleDialog || $[12] !== userVaultAssets || $[13] !== vaultAddress || $[14] !== vaultData) {
    t5 = () => {
      if (!isDefined(userVaultAssets)) {
        return;
      }
      toggleDialog(<SectionErrorBoundary fallback={<ErrorServiceUnavailable minHeight="494px" />}><VaultV1WithdrawReviewDialog vaultData={vaultData} vaultAddress={vaultAddress} inputAmount={inputAmount} isMax={isMax} onTxSuccess={onTxSuccess} entryPoint="vault_page" /></SectionErrorBoundary>);
    };
    $[8] = inputAmount;
    $[9] = isMax;
    $[10] = onTxSuccess;
    $[11] = toggleDialog;
    $[12] = userVaultAssets;
    $[13] = vaultAddress;
    $[14] = vaultData;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  const openReviewDialog = t5;
  const t6 = `Withdraw ${vaultAsset?.symbol}`;
  const t7 = maxWithdraw?.value;
  let t8;
  if ($[16] !== inputAmount || $[17] !== isLoading || $[18] !== isMax || $[19] !== onMaxClick || $[20] !== setInputAmount || $[21] !== t6 || $[22] !== t7 || $[23] !== vaultAsset) {
    t8 = <LargeAssetInput header={t6} token={vaultAsset} amount={inputAmount} onChange={setInputAmount} maxValue={t7} isMaxLoading={isLoading} isMax={isMax} onMaxClick={onMaxClick} autoFocus={true} />;
    $[16] = inputAmount;
    $[17] = isLoading;
    $[18] = isMax;
    $[19] = onMaxClick;
    $[20] = setInputAmount;
    $[21] = t6;
    $[22] = t7;
    $[23] = vaultAsset;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  let t9;
  if ($[25] !== error.userVaultPositionError) {
    t9 = <VaultV1FieldError error={error.userVaultPositionError} message="Error fetching vault position." />;
    $[25] = error.userVaultPositionError;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  let t10;
  if ($[27] !== finalPosition || $[28] !== isLoading || $[29] !== userVaultAssets || $[30] !== vaultAddress || $[31] !== vaultAsset || $[32] !== vaultData) {
    t10 = <VaultV1Summary vaultAsset={vaultAsset} vaultAddress={vaultAddress} vaultData={vaultData} initialPosition={userVaultAssets} finalPosition={finalPosition} isLoading={isLoading} />;
    $[27] = finalPosition;
    $[28] = isLoading;
    $[29] = userVaultAssets;
    $[30] = vaultAddress;
    $[31] = vaultAsset;
    $[32] = vaultData;
    $[33] = t10;
  } else {
    t10 = $[33];
  }
  const t11 = vaultAsset?.address;
  const t12 = !bundleCanTransfer;
  let t13;
  if ($[34] !== canTransfer || $[35] !== disabledLabel || $[36] !== isLoading || $[37] !== openReviewDialog || $[38] !== t10 || $[39] !== t11 || $[40] !== t12 || $[41] !== t8 || $[42] !== t9 || $[43] !== tooltipMessage) {
    t13 = <VaultV1OperationShell assetInput={t8} fieldErrors={t9} summary={t10} assetAddress={t11} canTransfer={canTransfer} isBundleNotAuthorized={t12} bundleNotAuthorizedMessage={WITHDRAW_BUNDLE_NOT_AUTHORIZED_MESSAGE} handleFinalize={openReviewDialog} isLoading={isLoading} reviewLabel="Review Withdraw" disabledLabel={disabledLabel} tooltipMessage={tooltipMessage} />;
    $[34] = canTransfer;
    $[35] = disabledLabel;
    $[36] = isLoading;
    $[37] = openReviewDialog;
    $[38] = t10;
    $[39] = t11;
    $[40] = t12;
    $[41] = t8;
    $[42] = t9;
    $[43] = tooltipMessage;
    $[44] = t13;
  } else {
    t13 = $[44];
  }
  return t13;
};
export default VaultV1OperationWithdraw;